import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { 
  TrendUp, 
  TrendDown,
  CheckCircle,
  Warning,
  Sparkle,
  Lightning,
  ArrowRight,
  Robot,
  CreditCard,
  ChartLineUp,
  Lightbulb,
  Target,
  CalendarBlank,
  ShieldCheck,
  ArrowsClockwise,
  MagnifyingGlass,
  Star,
  CaretDown,
  CaretUp
} from '@phosphor-icons/react';

const API_URL = (typeof window !== 'undefined' && window.location.hostname && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

const insightConfig = {
  urgent:     { icon: Warning,      color: 'text-[#ba1a1a]', bg: 'bg-[#fde7e9]', border: 'border-l-[#ba1a1a]' },
  warning:    { icon: Warning,      color: 'text-[#b45309]', bg: 'bg-[#fef3c7]', border: 'border-l-[#b45309]' },
  saving:     { icon: TrendDown,    color: 'text-[#0f6b3a]', bg: 'bg-[#dcfce7]', border: 'border-l-[#0f6b3a]' },
  trend_down: { icon: TrendDown,    color: 'text-[#0f6b3a]', bg: 'bg-[#dcfce7]', border: 'border-l-[#0f6b3a]' },
  trend_up:   { icon: TrendUp,      color: 'text-[#ba1a1a]', bg: 'bg-[#fde7e9]', border: 'border-l-[#ba1a1a]' },
  tip:        { icon: Lightbulb,    color: 'text-[#005bc1]', bg: 'bg-[#d6e3ff]', border: 'border-l-[#005bc1]' },
  benchmark:  { icon: ChartLineUp,  color: 'text-[#5b21b6]', bg: 'bg-[#ede7fe]', border: 'border-l-[#5b21b6]' },
  seasonal:   { icon: CalendarBlank,color: 'text-[#b45309]', bg: 'bg-[#fef3c7]', border: 'border-l-[#b45309]' },
  action:     { icon: Target,       color: 'text-[#005bc1]', bg: 'bg-[#d6e3ff]', border: 'border-l-[#005bc1]' },
};

const defaultConfig = { icon: Sparkle, color: 'text-[#596065]', bg: 'bg-[#f2f4f6]', border: 'border-l-[#596065]' };

export default function Insights() {
  const [insights, setInsights] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(new Set());
  const [optimizations, setOptimizations] = useState([]);
  const [optimRegion, setOptimRegion] = useState(null);
  const [optimLoading, setOptimLoading] = useState(false);
  const [optimMeta, setOptimMeta] = useState({ generated_at: null, from_cache: false, status: null });

  useEffect(() => {
    fetchData();
  }, []);

  // Poll for optimizations if backend reports "generating"
  useEffect(() => {
    if (optimMeta.status !== 'generating') return undefined;
    const tz = (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || '';
    const pollUrl = `${API_URL}/api/insights/optimizations${tz ? `?tz=${encodeURIComponent(tz)}` : ''}`;
    const id = setInterval(async () => {
      try {
        const { data } = await axios.get(pollUrl, { withCredentials: true });
        if (data?.status === 'ready') {
          setOptimizations(data?.optimizations || []);
          setOptimRegion(data?.region || null);
          setOptimMeta({
            generated_at: data?.generated_at || null,
            from_cache: data?.from_cache || false,
            status: 'ready',
          });
        }
      } catch {
        /* ignore — keep polling */
      }
    }, 5000);
    // Stop polling after 2 minutes even if still "generating"
    const stop = setTimeout(() => clearInterval(id), 120000);
    return () => {
      clearInterval(id);
      clearTimeout(stop);
    };
  }, [optimMeta.status]);

  const fetchData = async () => {
    // Always send browser's IANA timezone so the backend can locate the user
    // correctly even if they've never set a timezone in their profile.
    const tz = (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || '';
    const optimUrl = `${API_URL}/api/insights/optimizations${tz ? `?tz=${encodeURIComponent(tz)}` : ''}`;
    try {
      const [insightsRes, tasksRes, optimRes] = await Promise.all([
        axios.get(`${API_URL}/api/insights`, { withCredentials: true }),
        axios.get(`${API_URL}/api/tasks`, { withCredentials: true }),
        axios.get(optimUrl, { withCredentials: true }),
      ]);
      setInsights(insightsRes.data);
      setTasks(tasksRes.data);
      setOptimizations(optimRes.data?.optimizations || []);
      setOptimRegion(optimRes.data?.region || null);
      setOptimMeta({
        generated_at: optimRes.data?.generated_at || null,
        from_cache: optimRes.data?.from_cache || false,
        status: optimRes.data?.status || 'ready',
      });
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshOptimizations = async () => {
    setOptimLoading(true);
    const tz = (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || '';
    const url = `${API_URL}/api/insights/optimizations/refresh${tz ? `?tz=${encodeURIComponent(tz)}` : ''}`;
    try {
      const { data } = await axios.post(url, {}, { withCredentials: true, timeout: 120000 });
      setOptimizations(data?.optimizations || []);
      setOptimRegion(data?.region || null);
      setOptimMeta({
        generated_at: data?.generated_at || null,
        from_cache: false,
        status: 'ready',
      });
    } catch (error) {
      console.error('Optim refresh error:', error);
    } finally {
      setOptimLoading(false);
    }
  };

  const handleDismiss = (id) => {
    setDismissed(prev => new Set([...prev, id]));
  };

  const visibleInsights = insights.filter(i => !dismissed.has(i.insight_id));
  const urgentInsights = visibleInsights.filter(i => i.type === 'urgent' || i.type === 'warning');
  const trendInsights = visibleInsights.filter(i => i.type === 'trend_down' || i.type === 'trend_up');
  const aiInsights = visibleInsights.filter(i => ['tip', 'benchmark', 'seasonal', 'action', 'saving'].includes(i.type));

  // Compute spending summary from tasks - grouped by currency
  const pendingBills = tasks.filter(t => t.category === 'bill' && t.status === 'pending');
  const pendingSubs = tasks.filter(t => t.category === 'subscription' && t.status === 'pending');
  const completedBills = tasks.filter(t => t.category === 'bill' && t.status === 'completed');
  
  const groupByCurrency = (items) => {
    const groups = {};
    items.forEach(t => {
      if (!t.amount) return;
      const cur = t.currency || 'USD';
      groups[cur] = (groups[cur] || 0) + t.amount;
    });
    return groups;
  };

  const currencySymbols = {
    USD: 'US$', AUD: 'A$', SGD: 'S$', INR: '₹', EUR: '€', GBP: '£',
    NZD: 'NZ$', CAD: 'C$', JPY: '¥', CNY: '¥', KRW: '₩', MYR: 'RM',
  };
  const fmtAmt = (amount, currency) => {
    const sym = currencySymbols[currency] || (currency + ' ');
    return `${sym}${amount.toFixed(2)}`;
  };

  const billsByCur = groupByCurrency(pendingBills);
  const subsByCur = groupByCurrency(pendingSubs);
  const paidByCur = groupByCurrency(completedBills);

  // Group bills by provider for trend chart
  const providerData = {};
  tasks.filter(t => t.category === 'bill' && t.amount).forEach(t => {
    let p = (t.provider || 'Other').trim();
    p = p.replace(/^(My|Your)\s+/i, '').replace(/\s+(e-bill|alert|billing|services|pty|ltd).*$/i, '').trim();
    if (p.length > 20) p = p.split(' ').slice(0, 2).join(' ');
    if (!providerData[p]) providerData[p] = [];
    providerData[p].push({ amount: t.amount, date: t.due_date, status: t.status, currency: t.currency || 'USD' });
  });

  // Server-generated, region-aware optimizations (replaces hardcoded Australian brand list)

  return (
    <Layout>
      <div className="p-4 lg:p-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="display-lg text-4xl">Insights & Recommendations</h1>
          <p className="body-lg mt-2 max-w-2xl">
            AI-generated analysis of your bills, spending patterns, and actionable recommendations.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-24">
            <div className="animate-spin w-10 h-10 border-4 border-[#ebeef2] border-t-[#005bc1] rounded-full mx-auto mb-4"></div>
            <p className="body-lg">Analyzing your financial data...</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">

              {/* Spending Summary Cards */}
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="stat-card" data-testid="stat-bills-due">
                  <p className="label-md text-[#596065] mb-1">Bills Due</p>
                  {Object.entries(billsByCur).length > 0 ? Object.entries(billsByCur).sort((a,b) => b[1]-a[1]).map(([cur, total]) => (
                    <p key={cur} className="text-2xl font-bold text-[#2d3338]">{fmtAmt(total, cur)}</p>
                  )) : <p className="text-2xl font-bold text-[#2d3338]">-</p>}
                  <p className="text-sm text-[#596065] mt-1">{pendingBills.length} pending</p>
                </div>
                <div className="stat-card" data-testid="stat-subs-monthly">
                  <p className="label-md text-[#596065] mb-1">Subscriptions</p>
                  {Object.entries(subsByCur).length > 0 ? Object.entries(subsByCur).sort((a,b) => b[1]-a[1]).map(([cur, total]) => (
                    <p key={cur} className="text-2xl font-bold text-[#5b21b6]">{fmtAmt(total, cur)}</p>
                  )) : <p className="text-2xl font-bold text-[#5b21b6]">-</p>}
                  <p className="text-sm text-[#596065] mt-1">{pendingSubs.length} active /mo</p>
                </div>
                <div className="stat-card" data-testid="stat-paid-total">
                  <p className="label-md text-[#596065] mb-1">Paid This Period</p>
                  {Object.entries(paidByCur).length > 0 ? Object.entries(paidByCur).sort((a,b) => b[1]-a[1]).map(([cur, total]) => (
                    <p key={cur} className="text-2xl font-bold text-[#0f6b3a]">{fmtAmt(total, cur)}</p>
                  )) : <p className="text-2xl font-bold text-[#0f6b3a]">-</p>}
                  <p className="text-sm text-[#596065] mt-1">{completedBills.length} settled</p>
                </div>
              </div>

              {/* Urgent & Warnings */}
              {urgentInsights.length > 0 && (
                <div data-testid="urgent-insights-section">
                  <h2 className="title-md text-base mb-4 flex items-center gap-2">
                    <Warning size={20} className="text-[#ba1a1a]" weight="fill" />
                    Needs Attention
                  </h2>
                  <div className="space-y-3">
                    {urgentInsights.map((insight) => (
                      <InsightCard key={insight.insight_id} insight={insight} onDismiss={handleDismiss} />
                    ))}
                  </div>
                </div>
              )}

              {/* Bill Trends */}
              {trendInsights.length > 0 && (
                <div data-testid="trend-insights-section">
                  <h2 className="title-md text-base mb-4 flex items-center gap-2">
                    <ChartLineUp size={20} className="text-[#005bc1]" weight="fill" />
                    Bill Trends
                  </h2>
                  <div className="space-y-3">
                    {trendInsights.map((insight) => (
                      <InsightCard key={insight.insight_id} insight={insight} onDismiss={handleDismiss} />
                    ))}
                  </div>
                </div>
              )}

              {/* Provider Breakdown */}
              {Object.keys(providerData).length > 0 && (
                <div className="surface-card p-6" data-testid="provider-breakdown">
                  <h2 className="title-md text-base mb-5">Provider Breakdown</h2>
                  <div className="space-y-4">
                    {Object.entries(providerData)
                      .sort((a, b) => Math.max(...b[1].map(x => x.amount)) - Math.max(...a[1].map(x => x.amount)))
                      .map(([provider, bills]) => {
                        const amounts = bills.map(b => b.amount).sort((a, b) => a - b);
                        const latest = amounts[amounts.length - 1];
                        const oldest = amounts[0];
                        const maxAmt = Math.max(...Object.values(providerData).flat().map(b => b.amount));
                        const barWidth = (latest / maxAmt) * 100;
                        const hasTrend = amounts.length >= 2 && oldest !== latest;
                        const trendDown = hasTrend && latest < oldest;

                        return (
                          <div key={provider} className="flex items-center gap-4" data-testid={`provider-${provider}`}>
                            <div className="w-24 text-sm font-medium text-[#2d3338] truncate">{provider}</div>
                            <div className="flex-1">
                              <div className="h-8 bg-[#f2f4f6] rounded-lg overflow-hidden relative">
                                <div 
                                  className={`h-full rounded-lg transition-all ${trendDown ? 'bg-[#a3f5b8]' : hasTrend ? 'bg-[#ff8b9a]' : 'bg-[#d6e3ff]'}`}
                                  style={{ width: `${Math.max(barWidth, 8)}%` }}
                                ></div>
                              </div>
                            </div>
                            <div className="w-20 text-right">
                              <span className="font-bold text-sm text-[#2d3338]">{fmtAmt(latest, bills[0]?.currency || 'USD')}</span>
                              {hasTrend && (
                                <span className={`block text-xs ${trendDown ? 'text-[#0f6b3a]' : 'text-[#ba1a1a]'}`}>
                                  {trendDown ? '↓' : '↑'} {fmtAmt(Math.abs(latest - oldest), bills[0]?.currency || 'USD')}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Region-aware subscription & bill optimizations */}
              <div data-testid="optimization-section">
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                  <h2 className="title-md text-base flex items-center gap-2">
                    <MagnifyingGlass size={20} className="text-[#5b21b6]" weight="fill" />
                    Alternatives in your region
                    {optimRegion?.country_name && optimRegion.country_name !== 'global' && (
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider text-[#005bc1] bg-[#d6e3ff] px-2 py-0.5 rounded-full"
                        data-testid="optim-region-badge"
                      >
                        {optimRegion.country_name}
                      </span>
                    )}
                  </h2>
                  <button
                    onClick={refreshOptimizations}
                    disabled={optimLoading}
                    data-testid="refresh-optimizations-btn"
                    className="text-xs font-semibold text-[#005bc1] hover:text-[#004faa] disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <ArrowsClockwise size={12} className={optimLoading ? 'animate-spin' : ''} />
                    {optimLoading ? 'Searching…' : 'Refresh'}
                  </button>
                </div>

                {optimMeta.status === 'generating' ? (
                  <div
                    className="surface-card p-6 text-center"
                    data-testid="optim-generating"
                  >
                    <div className="animate-spin w-8 h-8 border-4 border-[#ebeef2] border-t-[#005bc1] rounded-full mx-auto mb-3" />
                    <p className="text-sm text-[#2d3338] font-semibold">
                      Searching your region for real alternatives…
                    </p>
                    <p className="text-xs text-[#596065] mt-1 max-w-md mx-auto">
                      We're running targeted web searches against your actual plans. This
                      usually takes 30–60 seconds on the first run; results are cached
                      afterwards.
                    </p>
                  </div>
                ) : optimizations.length > 0 ? (
                  <div className="space-y-5">
                    {optimizations.map((opt, idx) => (
                      <OptimizationCard key={idx} opt={opt} region={optimRegion} fmtAmt={fmtAmt} />
                    ))}
                  </div>
                ) : (
                  <div className="surface-card p-6 text-center">
                    <Lightbulb size={32} className="mx-auto mb-3 text-[#acb3b8]" weight="duotone" />
                    <p className="text-sm text-[#2d3338] font-semibold">No credible local alternatives found</p>
                    <p className="text-xs text-[#596065] mt-1 max-w-md mx-auto">
                      We only surface real, currently-operating competitors in your country. Either
                      your active services already look optimal, or we don't have enough inbox signal
                      yet. Try running a Gmail scan or hit Refresh.
                    </p>
                  </div>
                )}

                {optimMeta.generated_at && (
                  <p className="text-[10px] text-[#acb3b8] mt-2 text-right">
                    {optimMeta.from_cache ? 'Cached · ' : 'Generated · '}
                    {new Date(optimMeta.generated_at).toLocaleString()}
                  </p>
                )}
              </div>

              {/* AI Insights */}
              {aiInsights.length > 0 && (
                <div data-testid="ai-insights-section">
                  <h2 className="title-md text-base mb-4 flex items-center gap-2">
                    <Robot size={20} className="text-[#005bc1]" weight="fill" />
                    AI Recommendations
                  </h2>
                  <div className="space-y-3">
                    {aiInsights.map((insight) => (
                      <InsightCard key={insight.insight_id} insight={insight} onDismiss={handleDismiss} />
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {visibleInsights.length === 0 && (
                <div className="text-center py-16 surface-card">
                  <Sparkle size={48} className="mx-auto mb-4 text-[#acb3b8]" />
                  <p className="title-md">No insights yet</p>
                  <p className="body-lg text-sm mt-2 max-w-md mx-auto">
                    Add bills and tasks — the AI will analyze your spending patterns and generate personalized recommendations.
                  </p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* AI Summary */}
              <div className="stat-card-dark p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Robot size={18} weight="fill" className="text-white" />
                  <span className="label-md text-[#acb3b8]">AI Analysis</span>
                </div>
                <p className="text-sm text-white/90 leading-relaxed">
                  {visibleInsights.length > 0 
                    ? `Found ${visibleInsights.length} insights from your ${tasks.length} tasks. ${
                        trendInsights.some(i => i.type === 'trend_down') 
                          ? "Some bills are trending down — nice work." 
                          : urgentInsights.length > 0
                            ? "You have items that need immediate attention."
                            : "Your finances are looking manageable."
                      }`
                    : "No data to analyze yet. Connect Gmail or add tasks manually."
                  }
                </p>
              </div>

              {/* Monthly Snapshot */}
              <div className="stat-card">
                <p className="label-md text-[#596065] mb-3">Monthly Snapshot</p>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-[#596065]">Outstanding bills</span>
                    {Object.entries(billsByCur).sort((a,b) => b[1]-a[1]).map(([cur, total]) => (
                      <div key={cur} className="flex justify-between items-center">
                        <span className="text-xs text-[#acb3b8]">{cur}</span>
                        <span className="font-bold text-[#2d3338]">{fmtAmt(total, cur)}</span>
                      </div>
                    ))}
                    {Object.keys(billsByCur).length === 0 && (
                      <div className="text-right font-bold text-[#2d3338]">-</div>
                    )}
                  </div>
                  <div className="h-px bg-[#f2f4f6]"></div>
                  <div>
                    <span className="text-sm text-[#596065]">Monthly subscriptions</span>
                    {Object.entries(subsByCur).sort((a,b) => b[1]-a[1]).map(([cur, total]) => (
                      <div key={cur} className="flex justify-between items-center">
                        <span className="text-xs text-[#acb3b8]">{cur}</span>
                        <span className="font-bold text-[#5b21b6]">{fmtAmt(total, cur)}</span>
                      </div>
                    ))}
                    {Object.keys(subsByCur).length === 0 && (
                      <div className="text-right font-bold text-[#5b21b6]">-</div>
                    )}
                  </div>
                  <div className="h-px bg-[#f2f4f6]"></div>
                  <div>
                    <span className="text-sm text-[#596065]">Already paid</span>
                    {Object.entries(paidByCur).sort((a,b) => b[1]-a[1]).map(([cur, total]) => (
                      <div key={cur} className="flex justify-between items-center">
                        <span className="text-xs text-[#acb3b8]">{cur}</span>
                        <span className="font-bold text-[#0f6b3a]">{fmtAmt(total, cur)}</span>
                      </div>
                    ))}
                    {Object.keys(paidByCur).length === 0 && (
                      <div className="text-right font-bold text-[#0f6b3a]">-</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Tips */}
              <div className="stat-card">
                <p className="label-md text-[#596065] mb-3">Quick Tips</p>
                <div className="space-y-3 text-sm text-[#596065]">
                  <div className="flex gap-2">
                    <Lightning size={16} className="text-[#005bc1] flex-shrink-0 mt-0.5" weight="fill" />
                    <span>Mark bills as paid to track what's settled vs outstanding</span>
                  </div>
                  <div className="flex gap-2">
                    <Lightning size={16} className="text-[#005bc1] flex-shrink-0 mt-0.5" weight="fill" />
                    <span>Re-scan Gmail periodically to catch new bills</span>
                  </div>
                  <div className="flex gap-2">
                    <Lightning size={16} className="text-[#005bc1] flex-shrink-0 mt-0.5" weight="fill" />
                    <span>Multiple bills from the same provider unlock trend analysis</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}


function InsightCard({ insight, onDismiss }) {
  const config = insightConfig[insight.type] || defaultConfig;
  const Icon = config.icon;

  return (
    <div 
      className={`surface-card border-l-4 ${config.border} p-5 flex items-start gap-4`}
      data-testid={`insight-${insight.insight_id}`}
    >
      <div className={`w-10 h-10 rounded-xl ${config.bg} flex items-center justify-center flex-shrink-0`}>
        <Icon size={20} className={config.color} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[#2d3338]">{insight.title}</p>
        <p className="text-sm text-[#596065] mt-1 leading-relaxed">{insight.description}</p>
        {insight.amount_impact != null && (
          <p className="text-sm font-bold text-[#005bc1] mt-2">
            Impact: ${typeof insight.amount_impact === 'number' ? insight.amount_impact.toFixed(2) : insight.amount_impact}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {insight.action_label && (
          <button className="btn-primary text-xs px-3 py-1.5" data-testid={`insight-action-${insight.insight_id}`}>
            {insight.action_label}
          </button>
        )}
        <button 
          onClick={() => onDismiss(insight.insight_id)}
          className="p-1.5 hover:bg-[#f2f4f6] rounded-lg transition-colors text-[#acb3b8] hover:text-[#596065]"
          title="Dismiss"
          data-testid={`dismiss-insight-${insight.insight_id}`}
        >
          <span className="text-lg leading-none">&times;</span>
        </button>
      </div>
    </div>
  );
}



// ------------------------------------------------------------------
// Region-aware OptimizationCard (fed by backend /api/insights/optimizations).
// No hardcoded brands — everything comes from the server's web search +
// Claude analysis, scoped to the user's country.
// ------------------------------------------------------------------

function OptimizationCard({ opt, region, fmtAmt }) {
  const [expanded, setExpanded] = useState(true);

  const initials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).filter(Boolean).join('').toUpperCase().slice(0, 2);
  };

  const task = opt.task || {};
  const currency = task.currency || opt.savings_currency || 'USD';
  const monthlySavings = Number(opt.estimated_monthly_savings) || 0;
  const annualSavings = Number(opt.estimated_annual_savings) || monthlySavings * 12;
  const suggestions = Array.isArray(opt.alternatives) ? opt.alternatives : [];
  const providerName = opt.provider || task.provider || 'Unknown';
  const providerInitials = initials(providerName);

  const daysUntilDue = task.due_date
    ? Math.ceil((new Date(task.due_date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  const testId = task.task_id || providerName.replace(/\s+/g, '-').toLowerCase();

  return (
    <div className="surface-card overflow-hidden" data-testid={`optimization-${testId}`}>
      {/* Header */}
      <div className="p-5 pb-4">
        <div className="flex items-start justify-between mb-2 gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-[#2d3338]">{providerName}</h3>
              {opt.verdict === 'cancel_maybe' && (
                <span className="tag tag-error text-[10px]">Consider cancelling</span>
              )}
              {opt.verdict === 'switch' && (
                <span className="tag tag-primary text-[10px]">Cheaper option available</span>
              )}
              {(opt.verdict === 'keep' || opt.keep) && (
                <span className="tag tag-success text-[10px]">Already a good fit</span>
              )}
              {opt.usage_signal?.tag === 'active' && (
                <span className="text-[10px] font-bold text-[#0f6b3a] bg-[#dcfce7] px-2 py-0.5 rounded-full uppercase tracking-wider">
                  Active
                </span>
              )}
              {(opt.usage_signal?.tag === 'dormant' || opt.usage_signal?.tag === 'uncertain_new_or_one_off') && (
                <span className="text-[10px] font-bold text-[#8f5a29] bg-[#fbefe3] px-2 py-0.5 rounded-full uppercase tracking-wider">
                  Low usage signal
                </span>
              )}
            </div>
            {opt.current_summary && (
              <p className="text-sm text-[#596065] mt-1">{opt.current_summary}</p>
            )}
            {opt.usage_note && (
              <p className="text-xs text-[#596065] mt-1 flex items-center gap-1">
                <MagnifyingGlass size={11} weight="duotone" />
                {opt.usage_note}
              </p>
            )}
          </div>
          {daysUntilDue != null && daysUntilDue > 0 && daysUntilDue <= 30 && (
            <span className="tag tag-error text-[10px] flex-shrink-0">Due in {daysUntilDue}d</span>
          )}
        </div>

        {/* Projected Savings banner */}
        {opt.verdict !== 'keep' && !opt.keep && annualSavings > 0 && (
          <div className="mt-3 p-3 bg-gradient-to-r from-[#dcfce7] to-[#f0fdf4] rounded-xl flex items-center justify-between">
            <span className="text-xs font-semibold text-[#0f6b3a] uppercase tracking-wider">
              {opt.verdict === 'cancel_maybe' ? 'Potential savings if cancelled' : 'Estimated annual savings'}
            </span>
            <span
              className="text-lg font-bold text-[#0f6b3a]"
              data-testid={`savings-${testId}`}
            >
              {fmtAmt(annualSavings, currency)}
            </span>
          </div>
        )}

        {opt.reasoning && !opt.keep && (
          <p className="text-xs text-[#596065] mt-3 italic">{opt.reasoning}</p>
        )}
      </div>

      {/* Current Provider summary row */}
      {task.amount != null && (
        <div className="px-5 py-3 border-t border-[#f2f4f6]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#f2f4f6] flex items-center justify-center text-xs font-bold text-[#596065]">
              {providerInitials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-[#2d3338] truncate">{providerName}</span>
                <span className="text-[10px] font-bold text-[#596065] bg-[#f2f4f6] px-2 py-0.5 rounded-full uppercase">
                  Current
                </span>
              </div>
              {task.recurrence && task.recurrence !== 'none' && (
                <span className="text-xs text-[#596065] flex items-center gap-1 mt-0.5">
                  <ArrowsClockwise size={10} />
                  {task.recurrence} billing
                </span>
              )}
            </div>
            <div className="text-right">
              <p className="font-bold text-[#2d3338]">{fmtAmt(task.amount, currency)}</p>
              <p className="text-[10px] text-[#596065]">
                {task.recurrence === 'yearly' ? 'Annually' : 'Monthly'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Alternatives — only if there are real ones */}
      {suggestions.length > 0 && !opt.keep && (
        <>
          <div className="px-5 py-2 bg-[#f9fafb] border-t border-[#ebeef2] flex items-center justify-between">
            <span className="text-[10px] font-bold text-[#596065] uppercase tracking-wider">
              Alternatives in {region?.country_name || 'your region'}
            </span>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-[10px] font-semibold text-[#005bc1] hover:text-[#004faa]"
              data-testid={`toggle-alternatives-${testId}`}
            >
              {expanded ? 'Hide' : 'Show'}
            </button>
          </div>
          {expanded && suggestions.map((alt, i) => (
            <div
              key={i}
              className="px-5 py-3 border-t border-[#f2f4f6] hover:bg-[#f9fafb] transition-colors"
              data-testid={`alt-${testId}-${i}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xs font-bold ${
                  i === 0 ? 'bg-[#dcfce7] text-[#0f6b3a]' : 'bg-[#ede7fe] text-[#5b21b6]'
                }`}>
                  {initials(alt.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-[#2d3338]">{alt.name}</span>
                    {alt.pitch && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        i === 0 ? 'bg-[#dcfce7] text-[#0f6b3a]' : 'bg-[#ede7fe] text-[#5b21b6]'
                      }`}>
                        {alt.pitch}
                      </span>
                    )}
                    {alt.confidence && (
                      <span className="text-[10px] text-[#acb3b8] uppercase tracking-wider">
                        · {alt.confidence}
                      </span>
                    )}
                  </div>
                  {alt.source_url && (
                    <a
                      href={alt.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-[#005bc1] hover:underline mt-0.5 inline-block truncate max-w-full"
                      data-testid={`alt-source-${testId}-${i}`}
                    >
                      View source →
                    </a>
                  )}
                </div>
                {alt.estimated_monthly_cost != null && (
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-[#2d3338]">
                      {fmtAmt(alt.estimated_monthly_cost, currency)}
                    </p>
                    {task.amount && alt.estimated_monthly_cost < task.amount && (
                      <p className="text-xs font-semibold text-[#0f6b3a]">
                        -{fmtAmt(task.amount - alt.estimated_monthly_cost, currency)}/mo
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {/* No alternatives found — informative row */}
      {suggestions.length === 0 && !opt.keep && (
        <div className="px-5 py-3 border-t border-[#f2f4f6] text-xs text-[#596065] italic">
          We couldn't find a credible local alternative we'd confidently recommend.
        </div>
      )}
    </div>
  );
}
